/**
 * ConfigurationService - Centralized configuration management
 *
 * This service provides access to the pavement database configuration
 * using the Singleton pattern to ensure single source of truth across the application.
 *
 * Usage:
 *   const config = ConfigurationService.getInstance();
 *   const geogrids = config.getGeogrids();
 *   const materials = config.getMaterials();
 */

import db from './pavement-database.json';
import { lcrCalculationService } from './LcrCalculationService';
import { materialDatabaseService } from './MaterialDatabaseService';
import { ProductDataService } from './ProductDataService';

export interface IPavementMaterial {
    id: string;
    name: string;
    displayName?: string;
    coefficient_a1?: number;
    coefficient_a2?: number;
    coefficient_a3?: number;
    modulus_psi: number;
    minThickness_mm?: number;
    minThickness_m?: number;
    suggestedThickness_m?: number;
    materialType?: string;
    stability?: number;
    cbr?: number;
    ev2?: number;
    effectivePorosity?: number;
    permeabilityCoefficient?: number;
    permeability?: number;
    drainageQuality?: string;
    drainageCoefficient?: number;
    lcrValue?: number;
}

export interface IGeogridEquation {
    m2: number; // coefficient for CBR²
    m1: number; // coefficient for CBR
    c: number; // constant
    threshold: number; // CBR threshold value
}

export interface IGeogrid {
    id: string;
    name: string;
    drainageCoefficientOverride: number | null;
    equation?: IGeogridEquation | null; // Dynamic LCR calculation coefficients, null for "None" geogrid
    /** LCR value for base layer from DATABASE.txt H column. Used for geogrid stabilization calculations. */
    lcrBase?: number;
    /** LCR value for subbase layer from DATABASE.txt I column. Used for geogrid stabilization calculations. */
    lcrSubbase?: number;
    /** Geogrid grade (typically 20). Derived from equation threshold. */
    grade?: number;
}

export interface IPavementDatabase {
    info: {
        version: string;
        description: string;
        lastUpdated: string;
    };
    materials: {
        surface: IPavementMaterial[];
        base: IPavementMaterial[];
        subbase: IPavementMaterial[];
    };
    geogrids: IGeogrid[];
    drainage: {
        qualityMapping: Record<string, string>;
        coefficients: Record<string, Record<string, number>>;
    };
    aashtoFormulaConstants: {
        logW18: {
            logSnFactor: number;
            logSnConstant: number;
            logMrFactor: number;
            logMrConstant: number;
        };
        serviceability: {
            baseValue: number;
            failureValue: number;
            denominatorConstant: number;
            denominatorPowerFactor: number;
            snPower: number;
        };
    };
    designParameters: {
        defaults: {
            baseMaterialId: string;
            subbaseMaterialId: string;
            geogridId: string;
        };
        geogridDefaults?: {
            /** Default geogrid product name for Unpaved projects (Giroud-Han method) */
            unpaved: string;
            /** Default geogrid product name for Railway projects */
            railway: string;
            /** Default geogrid product family for Geogrid standalone projects */
            productFamily: string;
        };
        minimumThicknesses_m: {
            surface: number;
            base: number;
            subbase: number;
        };
        focusDesign: {
            baseFocus: {
                fixedSurfaceThickness_m: number;
                fixedSubbaseThickness_m: number;
            };
            subbaseFocus: {
                fixedSurfaceThickness_m: number;
                fixedBaseThickness_m: number;
            };
        };
        stabilization: {
            lcrThreshold: number;
        };
    };
    trafficAndReliability: {
        serviceability: {
            defaultInitial_po: number;
            defaultTerminal_pt: number;
        };
        standardErrors_So: Record<string, number>;
    };
    subgrade: {
        cbrToMrPsiMultiplier: number;
    };
}

export class ConfigurationService {
    private static instance: ConfigurationService;
    public readonly data: IPavementDatabase = db as IPavementDatabase;

    private constructor() {
        // Private constructor for Singleton pattern
    }

    public static getInstance(): ConfigurationService {
        if (!ConfigurationService.instance) {
            ConfigurationService.instance = new ConfigurationService();
        }
        return ConfigurationService.instance;
    }

    // Convenience methods for common data access

    public getMaterials() {
        return this.data.materials;
    }

    public getSurfaceMaterials(): IPavementMaterial[] {
        return this.data.materials.surface;
    }

    public getBaseMaterials(): IPavementMaterial[] {
        return this.data.materials.base;
    }

    public getSubbaseMaterials(): IPavementMaterial[] {
        return this.data.materials.subbase;
    }

    public getGeogrids(): IGeogrid[] {
        return this.data.geogrids;
    }

    public findGeogridById(id: string): IGeogrid | undefined {
        return this.data.geogrids.find(geogrid => geogrid.id === id);
    }

    public findMaterialById(type: 'surface' | 'base' | 'subbase', id: string): IPavementMaterial | undefined {
        return this.data.materials[type].find(material => material.id === id);
    }

    public getAASHTOConstants() {
        return this.data.aashtoFormulaConstants;
    }

    public getDesignParameters() {
        return this.data.designParameters;
    }

    /**
     * Get default geogrid product names for different project types.
     * These are used as initial values in form selects.
     * @returns Object with unpaved, railway, and productFamily defaults
     */
    public getGeogridDefaults() {
        return (
            this.data.designParameters.geogridDefaults ?? {
                unpaved: 'MACGRID WG 3S',
                railway: 'MACGRID EG 20S',
                productFamily: 'MacGrid WG',
            }
        );
    }

    public getDrainageMapping() {
        return this.data.drainage;
    }

    public getSubgradeParameters() {
        return this.data.subgrade;
    }

    public getTrafficParameters() {
        return this.data.trafficAndReliability;
    }

    // Helper methods for specific use cases

    /**
     * Bridge method for migration: accepts CBR parameter for more accurate LCR calculation
     * This method helps consumers migrate from the deprecated getGeogridLCR() to the new LcrCalculationService API
     *
     * @param geogridId Geogrid identifier
     * @param cbr CBR value for accurate calculation
     * @returns Dynamic LCR value based on CBR
     */
    public getGeogridLCRWithCBR(geogridId: string, cbr: number): number {
        return lcrCalculationService.calculateDynamicLCR(geogridId, cbr);
    }

    public getMaterialCoefficient(type: 'surface' | 'base' | 'subbase', materialId: string): number | undefined {
        const material = this.findMaterialById(type, materialId);
        if (!material) return undefined;

        switch (type) {
            case 'surface':
                return material.coefficient_a1;
            case 'base':
                return material.coefficient_a2;
            case 'subbase':
                return material.coefficient_a3;
            default:
                return undefined;
        }
    }

    public getMaterialModulus(type: 'surface' | 'base' | 'subbase', materialId: string): number | undefined {
        const material = this.findMaterialById(type, materialId);
        return material?.modulus_psi;
    }

    public getDrainageCoefficient(quality: string, moistureExposure: string): number {
        const coefficients = this.data.drainage.coefficients[quality];
        return coefficients ? coefficients[moistureExposure] || 1.0 : 1.0;
    }

    public getMinimumThicknesses() {
        return this.data.designParameters.minimumThicknesses_m;
    }

    public getFocusDesignParameters() {
        return this.data.designParameters.focusDesign;
    }

    public isStabilised(geogridId: string): boolean {
        const lcr = this.getGeogridLCRWithCBR(geogridId, 10); // Use default CBR for compatibility
        return lcr > this.data.designParameters.stabilization.lcrThreshold;
    }

    /**
     * Seleziona automaticamente la geogrid appropriata basandosi sul materiale
     * Updated to use MaterialDatabaseService for consistent material ID resolution
     */
    public getAutoSelectedGeogrid(layer: 'base' | 'subbase', materialId: string): string {
        // Get default material IDs from MaterialDatabaseService
        const defaultBaseMaterialId = materialDatabaseService.getDefaultBaseMaterial().id;
        const defaultSubbaseMaterialId = materialDatabaseService.getDefaultSubbaseMaterial().id;

        // Logica basata su Excel Design_LCR.txt
        if (layer === 'base') {
            if (materialId === defaultBaseMaterialId || materialId.includes('mixed_granular_soil_1')) {
                return 'MacGrid_EG_30S';
            }
        } else if (layer === 'subbase') {
            if (materialId === defaultSubbaseMaterialId || materialId.includes('naturally_stabilized_mix_2')) {
                return 'MacGrid_EG_40S';
            }
        }
        return 'None';
    }

    /**
     * Calcola l'LCR dinamico basandosi sull'equazione quadratica
     * Formula Excel: IF(CBR<threshold, m2*CBR^2+m1*CBR+c, m2*threshold^2+m1*threshold+c)
     */
    public calculateDynamicLCR(geogridId: string, cbrValue: number): number {
        const geogrid = this.findGeogridById(geogridId);

        if (!geogrid || !geogrid.equation) {
            // Se non ha equazione, usa l'LCR statico con CBR di default
            return this.getGeogridLCRWithCBR(geogridId, 10);
        }

        const { m2, m1, c, threshold } = geogrid.equation;
        const cbr = cbrValue <= threshold ? cbrValue : threshold;

        // Formula quadratica: m2*CBR^2 + m1*CBR + c
        const dynamicLCR = m2 * (cbr * cbr) + m1 * cbr + c;

        return Math.max(1.0, dynamicLCR); // LCR non può essere < 1.0
    }

    /**
     * Ottiene l'LCR per base e subbase con selezione automatica geogrids
     * Updated to use dynamic CBR calculation instead of deprecated methods
     */
    public getAutoLCRValues(baseMaterialId: string, subbaseMaterialId: string): { baseLCR: number; subbaseLCR: number } {
        const baseGeogridId = this.getAutoSelectedGeogrid('base', baseMaterialId);
        const subbaseGeogridId = this.getAutoSelectedGeogrid('subbase', subbaseMaterialId);

        // Get dynamic CBR values from materials for accurate LCR calculation
        const subbaseCBR = materialDatabaseService.getSubbaseCBR(subbaseMaterialId) || 10; // Excel default

        return {
            baseLCR: this.getGeogridLCRWithCBR(baseGeogridId, subbaseCBR),
            subbaseLCR: this.getGeogridLCRWithCBR(subbaseGeogridId, subbaseCBR),
        };
    }

    /**
     * Calcola LCR dinamici per base e subbase usando i rispettivi CBR
     */
    public getDynamicLCRValues(baseGeogridId: string, subbaseGeogridId: string, baseCBR: number, subbaseCBR: number): { baseLCR: number; subbaseLCR: number } {
        return {
            baseLCR: this.calculateDynamicLCR(baseGeogridId, baseCBR),
            subbaseLCR: this.calculateDynamicLCR(subbaseGeogridId, subbaseCBR),
        };
    }

    /**
     * Get geogrid options for base course from unified product database
     * Filters by applicableProjects: 'paved' and category: 'GEOGRID'
     * Base layer excludes Multicomposite family (only MacGrid EG)
     * Source of truth: geogrid-products-unified.json
     */
    public getBaseGeogridOptions(): { value: string; label: string }[] {
        // Filter products by projectType and category from unified database
        const pavedGeogrids = ProductDataService.getProductsWithFilters({
            projectType: 'paved',
            category: 'GEOGRID',
        });
        const options: { value: string; label: string }[] = [];

        // Base layer: only MacGrid EG family (excludes Multicomposite)
        for (const [familyName, products] of Object.entries(pavedGeogrids)) {
            if (familyName === 'Multicomposite') continue; // Multicomposite only for subbase

            for (const product of products) {
                // Use lowercase ID with underscores for backward compatibility
                const id = product.prodotto.toLowerCase().replace(/\s+/g, '_');
                options.push({ value: id, label: product.prodotto });
            }
        }

        // Fallback if no products found (should not happen in production)
        if (options.length === 0) {
            console.warn('ConfigurationService: No base geogrid options found for paved project');
        }

        return options;
    }

    /**
     * Get geogrid options for subbase course from unified product database
     * Filters by applicableProjects: 'paved' and category: 'GEOGRID'
     * Subbase layer includes all families (MacGrid EG + Multicomposite)
     * Source of truth: geogrid-products-unified.json
     */
    public getSubbaseGeogridOptions(): { value: string; label: string }[] {
        // Filter products by projectType and category from unified database
        const pavedGeogrids = ProductDataService.getProductsWithFilters({
            projectType: 'paved',
            category: 'GEOGRID',
        });
        const options: { value: string; label: string }[] = [];

        // Subbase layer: all families (MacGrid EG + Multicomposite)
        for (const [familyName, products] of Object.entries(pavedGeogrids)) {
            for (const product of products) {
                // MacGrid EG uses lowercase ID with underscores for backward compatibility
                // Multicomposite uses prodotto directly
                const id = familyName === 'Multicomposite'
                    ? product.prodotto
                    : product.prodotto.toLowerCase().replace(/\s+/g, '_');
                options.push({ value: id, label: product.prodotto });
            }
        }

        // Fallback if no products found (should not happen in production)
        if (options.length === 0) {
            console.warn('ConfigurationService: No subbase geogrid options found for paved project');
        }

        return options;
    }

    /**
     * Check if a geogrid ID is valid for the specified layer
     */
    public isValidGeogridForLayer(geogridId: string, layer: 'base' | 'subbase'): boolean {
        const options = layer === 'base' ? this.getBaseGeogridOptions() : this.getSubbaseGeogridOptions();
        return options.some(option => option.value === geogridId);
    }
}

export default ConfigurationService;
